import { useState, useCallback } from 'react';

import {
    getPatientMedications,
    fetchMedicationByUUID,
    createMedication,
    startMedication,
    deleteMedication
} from '../services/medication';

export const useMedications = () => {
    const [medications, setMedications] = useState([]);
    const [selectedMedication, setSelectedMedication] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadPatientMedications = useCallback(
        async (patientUUID, options = { onlyStarted: false, includeArchived: false }) => {
            setLoading(true);
            setError(null);

            try {
                const data = await getPatientMedications(
                    patientUUID,
                    options.onlyStarted,
                    options.includeArchived
                );
                setMedications(data);
                return data;
            } catch (err) {
                setError(err);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const loadMedicationByUUID = useCallback(async (uuid) => {
        setLoading(true);
        setError(null);

        try {
            const data = await fetchMedicationByUUID(uuid);
            setSelectedMedication(data);
            return data;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const addMedication = useCallback(async (payload) => {
        setLoading(true);
        setError(null);

        try {
            const newMedication = await createMedication(payload);
            setMedications((prev) => [...prev, newMedication]);
            return newMedication;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const startMedicationTreatment = useCallback(async (uuid, ts = null) => {
        setLoading(true);
        setError(null);

        try {
            await startMedication(uuid, ts);

            setMedications((prev) =>
                prev.map((med) =>
                    med.uuid === uuid
                        ? {
                              ...med,
                              started: true,
                              start_ts: ts ?? new Date().toISOString()
                          }
                        : med
                )
            );
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const archiveMedication = useCallback(async (uuid) => {
        setLoading(true);
        setError(null);

        try {
            await deleteMedication(uuid);

            setMedications((prev) =>
                prev.map((med) => (med.uuid === uuid ? { ...med, archived: true } : med))
            );
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        medications,
        selectedMedication,
        loading,
        error,

        loadPatientMedications,
        loadMedicationByUUID,
        addMedication,
        startMedicationTreatment,
        archiveMedication,

        setSelectedMedication,
        setMedications
    };
};
